import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, StatusPill } from '@agms/ui'
import type { StatusTone } from '@agms/ui'
import { usePermissions } from '../../../hooks/useAuth'
import { apiErrorMessage } from '../../../hooks/useErrorHandler'
import api from '../../../lib/api'
import type { PaginatedResponse } from '../../../lib/api'
import { accountGroupReasonLabel } from '../../../lib/accountGroups'
import type { AccountGroupRecord, RelatedAccountGroup } from '../../../lib/accountGroups'
import { SurfaceCard } from '../../ui'

function statusTone(status: string): StatusTone {
  if (status === 'active') return 'success'
  if (status === 'suspended') return 'warning'
  if (status === 'terminated') return 'danger'
  return 'neutral'
}

/**
 * Groups for one merchant, and the other accounts in those groups
 * that this sign-in can already open. The list is the whole response.
 * It does not add a count of accounts that were left out.
 */
export function RelatedAccountsPanel({ merchantId }: { merchantId: string }) {
  const queryClient = useQueryClient()
  const { hasRole } = usePermissions()
  const canManage = hasRole(['management', 'super_admin'])
  const [groupId, setGroupId] = useState('')
  const [error, setError] = useState<string | null>(null)

  const groupsQuery = useQuery({
    queryKey: ['merchant-account-groups', merchantId],
    queryFn: async () => {
      const response = await api.get<{ data: RelatedAccountGroup[] }>(`/merchants/${merchantId}/account-groups`)
      return response.data.data
    },
  })

  const allGroupsQuery = useQuery({
    queryKey: ['account-groups', 'picker'],
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<AccountGroupRecord>>('/account-groups', {
        params: { per_page: 100 },
      })
      return response.data
    },
    enabled: canManage,
  })

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['merchant-account-groups', merchantId] })
    await queryClient.invalidateQueries({ queryKey: ['account-groups'] })
    await queryClient.invalidateQueries({ queryKey: ['merchants'] })
  }

  const addMutation = useMutation({
    mutationFn: async (accountGroupId: string) => {
      await api.post(`/account-groups/${accountGroupId}/members`, { merchant_id: merchantId })
    },
    onSuccess: async () => {
      setGroupId('')
      setError(null)
      await invalidate()
    },
    onError: (caught) => {
      setError(apiErrorMessage(caught, 'Could not add this account to the group.'))
    },
  })

  const removeMutation = useMutation({
    mutationFn: async (accountGroupId: string) => {
      await api.delete(`/account-groups/${accountGroupId}/members/${merchantId}`)
    },
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: (caught) => {
      setError(apiErrorMessage(caught, 'Could not remove this account from the group.'))
    },
  })

  const groups = groupsQuery.data ?? []
  const joined = new Set(groups.map((group) => group.id))
  const available = (allGroupsQuery.data?.data ?? []).filter((group) => !joined.has(group.id))

  return (
    <SurfaceCard padding="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-950">Related accounts</h2>
          <p className="mt-1 text-sm text-slate-500">Groups this account belongs to.</p>
        </div>
        {canManage && (
          <Link to="/account-groups" className="text-sm font-semibold text-blue-700 hover:text-blue-900">
            Manage groups
          </Link>
        )}
      </div>

      {groupsQuery.isLoading && <p className="mt-4 text-sm text-slate-500">Loading related accounts...</p>}

      {groupsQuery.isError && (
        <p className="mt-4 text-sm text-rose-700" role="alert">Could not load related accounts.</p>
      )}

      {!groupsQuery.isLoading && !groupsQuery.isError && groups.length === 0 && (
        <p className="mt-4 text-sm text-slate-500">This account is not in a group.</p>
      )}

      {groups.length > 0 && (
        <ul className="mt-4 space-y-4">
          {groups.map((group) => (
            <li key={group.id} className="rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <Link to={`/account-groups/${group.id}`} className="font-semibold text-slate-950 hover:text-blue-800">
                    {group.name}
                  </Link>
                  <p className="text-sm text-slate-500">{accountGroupReasonLabel(group.reason_type)}</p>
                  {canManage && group.note ? <p className="mt-1 text-sm text-slate-600">{group.note}</p> : null}
                </div>
                {canManage && (
                  <Button
                    variant="secondary"
                    onClick={() => removeMutation.mutate(group.id)}
                    disabled={removeMutation.isPending}
                  >
                    {`Remove this account from ${group.name}`}
                  </Button>
                )}
              </div>
              {group.related_accounts.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {group.related_accounts.map((account) => (
                    <li key={account.id} className="flex flex-wrap items-center gap-2 text-sm">
                      <Link to={`/merchants/${account.id}`} className="font-semibold text-blue-700 hover:text-blue-900">
                        {account.dba_name}
                      </Link>
                      <StatusPill tone={statusTone(account.status)} className="capitalize">
                        {account.status}
                      </StatusPill>
                      {account.fiserv_mid ? (
                        <span className="font-mono text-xs text-slate-500">MID {account.fiserv_mid}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <form
          className="mt-4 flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            if (!groupId) return
            addMutation.mutate(groupId)
          }}
        >
          <label className="text-sm text-slate-700">
            <span className="mb-1 block font-medium">Add to a group</span>
            <select
              aria-label="Add to a group"
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={groupId}
              onChange={(event) => setGroupId(event.target.value)}
            >
              <option value="">Choose a group</option>
              {available.map((group) => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </label>
          <Button type="submit" variant="secondary" disabled={!groupId || addMutation.isPending}>
            Add
          </Button>
        </form>
      )}

      {error && <p className="mt-3 text-sm text-rose-700" role="alert">{error}</p>}
    </SurfaceCard>
  )
}
